import { ReactNode, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import useGetTableData from "../../hook/useGetTableData";
import { useAppContext } from "../appContext/AppContext";
import TableTemplate from "../component/table-pagination";
import PatientInfoModal from "../patients/components/patient-info-modal";
import { getMedicalRecordLink } from "../patients/services/PatientServices";
import { asyncWrapper } from "../utils/AppFunction";
import {
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZE,
	MEDICAL_RECORD_SYSTEM,
} from "../utils/Constant";
import Filter from "./components/Filter";
import MedicalRecordInfoModal from "./components/medical-record-info-modal";
import { MEDICAL_RECORD_COLUMNS } from "./constants/columns";
import { getMedicalRecords } from "./services";

type ColumnActionType = "viewPatient" | "viewMedicalRecord";

const MedicalRecords = () => {
	const { setIsLoading } = useAppContext();
	const { query, setQuery, data, paging } = useGetTableData({
		fetchApi: getMedicalRecords,
		initialQuery: {
			pageIndex: DEFAULT_PAGE_INDEX,
			pageSize: DEFAULT_PAGE_SIZE,
		},
	});

	const [dynamicComponent, setDynamicComponent] = useState<{
		key: string;
		data: any;
	}>({
		key: "",
		data: null,
	});

	const tableData = useMemo(() => {
		return data?.content
			? data?.content.map((item: any, index: number) => {
					return {
						...item,
						order:
							(query?.pageIndex > 1 ? query.pageIndex - 1 : 0) *
								query?.pageSize +
							index +
							1,
					};
			  })
			: [];
	}, [data]);

	const onModalClose = () => {
		setDynamicComponent({
			key: "",
			data: null,
		});
	};

	const onActionButtonClick = (rowData: any, actionType: ColumnActionType) => {
		if (actionType === "viewMedicalRecord") {
			viewMedicalRecord(rowData);
			return;
		}

		setDynamicComponent({
			key: actionType,
			data: rowData,
		});
	};

	const viewMedicalRecord = async (row: any) => {
		const [data] = await asyncWrapper(
			getMedicalRecordLink({
				medicalRecordCode: row.medicalRecordCode,
				system: MEDICAL_RECORD_SYSTEM.ELSAGA,
			}),
			setIsLoading
		);

		if (!data) return;

		window.open(data, "_blank");
	};

	const DynamicComponent = useMemo(() => {
		const componentMap: Record<Exclude<ColumnActionType, 'viewMedicalRecord'>, ReactNode> = {
			viewPatient: <PatientInfoModal open onClose={onModalClose} />,
		};
		return componentMap[dynamicComponent.key as keyof typeof componentMap];
	}, [dynamicComponent.key]);

	return (
		<Card className="p-3">
			<Filter
				onSearch={(params) => {
					setQuery({
						...query,
						...params,
						pageIndex: DEFAULT_PAGE_INDEX,
					});
				}}
			/>
			<TableTemplate
				data={tableData}
				columns={MEDICAL_RECORD_COLUMNS}
				paging={{
					...paging,
					page: query.pageIndex,
					rowsPerPage: query.pageSize,
				}}
				selectable={false}
				tableClassname="h-calc-vh-200"
				onPagingChange={(page, size) => {
					setQuery({
						...query,
						pageIndex: page,
						pageSize: size,
					});
				}}
				tableOptions={{
					onActionButtonClick,
				}}
			/>
			{dynamicComponent.key && DynamicComponent}
		</Card>
	);
};

export default MedicalRecords;
